import React, { useState, useEffect, useCallback } from 'react';

/**
 * Country code entries for the dropdown.
 * Sorted by most common for US mental health clinics, then alphabetical.
 */
const COUNTRY_CODES = [
  { code: '+1', label: 'US/CA', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: '+44', label: 'UK', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: '+61', label: 'AU', flag: '\u{1F1E6}\u{1F1FA}' },
  { code: '+33', label: 'FR', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: '+49', label: 'DE', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: '+52', label: 'MX', flag: '\u{1F1F2}\u{1F1FD}' },
  { code: '+55', label: 'BR', flag: '\u{1F1E7}\u{1F1F7}' },
  { code: '+81', label: 'JP', flag: '\u{1F1EF}\u{1F1F5}' },
  { code: '+82', label: 'KR', flag: '\u{1F1F0}\u{1F1F7}' },
  { code: '+86', label: 'CN', flag: '\u{1F1E8}\u{1F1F3}' },
  { code: '+91', label: 'IN', flag: '\u{1F1EE}\u{1F1F3}' },
  { code: '+353', label: 'IE', flag: '\u{1F1EE}\u{1F1EA}' },
  { code: '+64', label: 'NZ', flag: '\u{1F1F3}\u{1F1FF}' },
  { code: '+65', label: 'SG', flag: '\u{1F1F8}\u{1F1EC}' },
  { code: '+972', label: 'IL', flag: '\u{1F1EE}\u{1F1F1}' },
] as const;

type CountryCode = typeof COUNTRY_CODES[number]['code'];

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  error?: string | null;
  disabled?: boolean;
  name?: string;
  id?: string;
  label?: string;
}

/**
 * Detect country code from an E.164 number or raw input.
 * Returns the code and the local number portion.
 */
function parsePhoneValue(value: string): { countryCode: CountryCode; localNumber: string } {
  if (!value) return { countryCode: '+1', localNumber: '' };

  // If value starts with +, try to match a country code
  if (value.startsWith('+')) {
    // Sort by code length descending so longer codes match first (+353 before +3)
    const sorted = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
    for (const entry of sorted) {
      if (value.startsWith(entry.code)) {
        return { countryCode: entry.code, localNumber: value.slice(entry.code.length) };
      }
    }
    // Unknown country code — default to +1 and show full value
    return { countryCode: '+1', localNumber: value.slice(1) };
  }

  // Raw digits or formatted US number — default to +1
  return { countryCode: '+1', localNumber: value };
}

/**
 * PhoneInput — reusable phone number input with country code dropdown.
 *
 * Accepts E.164 values and raw digits. Reports onChange with the full
 * combined value (country code + local number digits) suitable for
 * backend normalization.
 */
export function PhoneInput({
  value,
  onChange,
  placeholder = '(555) 123-4567',
  className = '',
  error,
  disabled = false,
  name,
  id,
  label,
}: PhoneInputProps) {
  const parsed = parsePhoneValue(value);
  const [countryCode, setCountryCode] = useState<CountryCode>(parsed.countryCode);
  const [localNumber, setLocalNumber] = useState(parsed.localNumber);

  // Sync from external value changes (e.g. form reset)
  useEffect(() => {
    const p = parsePhoneValue(value);
    setCountryCode(p.countryCode);
    setLocalNumber(p.localNumber);
  }, [value]);

  const emitChange = useCallback((code: CountryCode, local: string) => {
    if (!local.trim()) {
      onChange('');
      return;
    }
    // Emit as country code + digits for backend normalization
    const digits = local.replace(/\D/g, '');
    if (digits) {
      onChange(`${code}${digits}`);
    } else {
      onChange('');
    }
  }, [onChange]);

  const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLocal = e.target.value;
    setLocalNumber(newLocal);
    emitChange(countryCode, newLocal);
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCode = e.target.value as CountryCode;
    setCountryCode(newCode);
    emitChange(newCode, localNumber);
  };

  const baseInputClass = 'flex-1 min-w-0 px-3 py-2 text-sm rounded-r-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white border focus:outline-none focus:ring-1 focus:ring-blue-500';
  const borderClass = error
    ? 'border-red-400 dark:border-red-500'
    : 'border-gray-300 dark:border-gray-600';

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}
      <div className="flex">
        <select
          value={countryCode}
          onChange={handleCountryChange}
          disabled={disabled}
          className="px-2 py-2 text-sm rounded-l-md bg-gray-50 dark:bg-gray-600 text-gray-700 dark:text-gray-200 border border-r-0 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[72px]"
          aria-label="Country code"
        >
          {COUNTRY_CODES.map((entry) => (
            <option key={entry.code} value={entry.code}>
              {entry.flag} {entry.code}
            </option>
          ))}
        </select>
        <input
          type="tel"
          id={id}
          name={name}
          value={localNumber}
          onChange={handleLocalChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`${baseInputClass} ${borderClass}`}
          aria-invalid={!!error}
        />
      </div>
      {error && (
        <p className="mt-1 text-xs text-red-500 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}

export { COUNTRY_CODES, parsePhoneValue };
export type { PhoneInputProps, CountryCode };
